import apiService from '../apiService';

/**
 * 獲取所有角色列表
 * @param {Object} params - 查詢參數
 * @param {number} params.page - 頁碼
 * @param {number} params.per_page - 每頁筆數
 * @returns {Promise} API 響應
 */
export const getRoles = async (params = {}) => {
    const url = '/api/admin/roles';
    return apiService.GET(url, params);
};

/**
 * 獲取單一角色詳情
 * @param {number} roleId - 角色 ID
 * @returns {Promise} API 響應
 */
export const getRoleDetail = async (roleId) => {
    const url = `/api/admin/roles/${roleId}`;
    return apiService.GET(url);
};

/**
 * 獲取所有權限列表
 * @returns {Promise} API 響應
 */
export const getPermissions = async () => {
    const url = '/api/admin/permissions';
    return apiService.GET(url);
};

/**
 * 資料庫實際存在的權限（manager-api），供角色送出時過濾
 */
export const getPermissionList = async (guard = 'manager-api') => {
    const url = '/api/admin/permissions/list';
    return apiService.GET(url, { guard });
};

/**
 * 獲取權限名稱列表
 * @returns {Promise} API 響應
 */
export const getPermissionNames = async () => {
    const url = '/api/admin/permissions/name';
    return apiService.GET(url);
};

/**
 * 獲取單一權限詳情
 * @param {number} permissionId - 權限 ID
 * @returns {Promise} API 響應
 */
export const getPermissionDetail = async (permissionId) => {
    const url = `/api/admin/permissions/${permissionId}`;
    return apiService.GET(url);
};

/**
 * 建立角色
 * @param {Object} roleData - 角色資料
 * @param {string} roleData.name - 角色名稱
 * @param {string} roleData.description - 角色描述
 * @returns {Promise} API 響應
 */
export const createRole = async (roleData) => {
    const url = '/api/admin/roles';
    return apiService.POST(url, roleData);
};

/**
 * 更新角色
 * @param {number} roleId - 角色 ID
 * @param {Object} roleData - 要更新的角色資料
 * @returns {Promise} API 響應
 */
export const updateRole = async (roleId, roleData) => {
    const url = `/api/admin/roles/${roleId}`;
    return apiService.PUT(url, roleData);
};

/**
 * 刪除角色
 * @param {number} roleId - 角色 ID
 * @returns {Promise} API 響應
 */
export const deleteRole = async (roleId) => {
    const url = `/api/admin/roles/${roleId}`;
    return apiService.DELETE(url);
};

/**
 * 建立權限
 * @param {Object} permissionData - 權限資料
 * @param {string} permissionData.name - 權限名稱
 * @param {string} permissionData.description - 權限描述
 * @returns {Promise} API 響應
 */
export const createPermission = async (permissionData) => {
    const url = '/api/admin/permissions';
    return apiService.POST(url, permissionData);
};

/**
 * 更新權限
 * @param {number} permissionId - 權限 ID
 * @param {Object} permissionData - 要更新的權限資料
 * @returns {Promise} API 響應
 */
export const updatePermission = async (permissionId, permissionData) => {
    const url = `/api/admin/permissions/${permissionId}`;
    return apiService.PUT(url, permissionData);
};

/**
 * 刪除權限
 * @param {number} permissionId - 權限 ID
 * @returns {Promise} API 響應
 */
export const deletePermission = async (permissionId) => {
    const url = `/api/admin/permissions/${permissionId}`;
    return apiService.DELETE(url);
};

// ========== 權限管理 API ==========

/**
 * 取得權限階層結構
 * @returns {Promise} API 響應
 */
export const getPermissionHierarchy = async () => {
    const url = '/api/admin/permissions';
    return apiService.GET(url);
};

/**
 * 取得特定權限詳細資訊
 * @param {string} permissionCode - 權限代碼
 * @returns {Promise} API 響應
 */
export const getPermissionByCode = async (permissionCode) => {
    const url = `/api/admin/permissions/${permissionCode}`;
    return apiService.GET(url);
};

// ========== 用戶角色管理 ==========

/**
 * 指派角色給管理者
 * @param {number} managerId - 管理者 ID
 * @param {Object} roleData - 角色資料
 * @param {number} roleData.role_id - 角色 ID
 * @returns {Promise} API 響應
 */
export const assignRoleToManager = async (managerId, roleData) => {
    const url = `/api/admin/user-roles/manager/${managerId}/roles`;
    return apiService.POST(url, roleData);
};

/**
 * 移除管理者角色
 * @param {number} managerId - 管理者 ID
 * @param {number} roleId - 角色 ID
 * @returns {Promise} API 響應
 */
export const removeManagerRole = async (managerId, roleId) => {
    const url = `/api/admin/user-roles/manager/${managerId}/role/${roleId}`;
    return apiService.DELETE(url);
};

/**
 * 指派角色給會員
 * @param {number} userId - 會員 ID
 * @param {Object} roleData - 角色資料
 * @param {number} roleData.role_id - 角色 ID
 * @returns {Promise} API 響應
 */
export const assignRoleToUser = async (userId, roleData) => {
    const url = `/api/admin/user-roles/user/${userId}/roles`;
    return apiService.POST(url, roleData);
};

/**
 * 取得會員所有權限
 * @param {number} userId - 會員 ID
 * @returns {Promise} API 響應
 */
export const getUserPermissions = async (userId) => {
    const url = `/api/admin/user-roles/user/${userId}/permissions`;
    return apiService.GET(url);
};

/**
 * 移除會員角色
 * @param {number} userId - 會員 ID
 * @param {number} roleId - 角色 ID
 * @returns {Promise} API 響應
 */
export const removeUserRole = async (userId, roleId) => {
    const url = `/api/admin/user-roles/user/${userId}/role/${roleId}`;
    return apiService.DELETE(url);
};
